import * as AlertDialog from "@radix-ui/react-alert-dialog";
import * as Dialog from "@radix-ui/react-dialog";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import type { Project } from "../../api/types";
import { Spinner } from "../../ui/Spinner";
import { toast } from "../../ui/toast";
import { btn, overlay, panel } from "../chat/ChatDialogs";
import { useDeleteProject, useSaveProject } from "./queries";

/** Как PROJECT_LIMITS в @hermes/shared: сам пакет с zod в основной бандл не тянем. */
const PROJECT_LIMITS = { name: 120, instructions: 8_000 };

export const field =
  "w-full rounded-control border-[1.5px] border-line bg-surface px-4 text-base focus:border-accent focus:shadow-[0_0_0_4px_rgb(23_128_60/14%)] focus:outline-none focus-visible:outline-none";

/** Создание проекта (project = null) или правка названия и инструкций. */
export function ProjectDialog({
  open,
  project,
  focus = "name",
  onClose,
  onSaved,
}: {
  open: boolean;
  project: Project | null;
  focus?: "name" | "instructions";
  onClose: () => void;
  onSaved?: (p: Project) => void;
}) {
  const save = useSaveProject();
  const [name, setName] = useState("");
  const [instructions, setInstructions] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(project?.name ?? "");
    setInstructions(project?.instructions ?? "");
  }, [open, project]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || save.isPending) return;
    save.mutate(
      { ...(project ? { id: project.id } : {}), name: name.trim(), instructions: instructions.trim() },
      {
        onSuccess: (p) => {
          onClose();
          onSaved?.(p);
        },
        onError: () => toast(project ? "Не удалось сохранить проект. Попробуйте ещё раз." : "Не удалось создать проект. Попробуйте ещё раз."),
      },
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className={overlay} />
        <Dialog.Content className={`${panel} max-w-[520px]`} aria-describedby="project-dialog-desc">
          <Dialog.Title className="text-lg font-bold">{project ? "Настройки проекта" : "Новый проект"}</Dialog.Title>
          <p id="project-dialog-desc" className="mt-1.5 text-sm leading-5 text-sub">
            Чаты проекта собраны в одном месте, а ассистент в них помнит ваши инструкции и видит файлы проекта.
          </p>
          <form onSubmit={submit} className="mt-4 space-y-4">
            <div>
              <label htmlFor="project-name" className="mb-1.5 block text-sm font-semibold">
                Название
              </label>
              <input
                id="project-name"
                autoFocus={focus === "name"}
                maxLength={PROJECT_LIMITS.name}
                placeholder="Например, «Бюджет 2027»"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`${field} h-12`}
              />
            </div>
            <div>
              <label htmlFor="project-instructions" className="mb-1.5 block text-sm font-semibold">
                Инструкции для ассистента <span className="font-normal text-sub">— необязательно</span>
              </label>
              <textarea
                id="project-instructions"
                autoFocus={focus === "instructions"}
                rows={6}
                maxLength={PROJECT_LIMITS.instructions}
                placeholder="Контекст, тон и формат ответов. Например: «Мы готовим бюджет на 2027 год. Суммы — в млн ₽, выводы — списком»."
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                className={`${field} block resize-y py-3 text-[15px] leading-[22px]`}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Dialog.Close className={`${btn} text-ink-2 hover:bg-bg`}>Отмена</Dialog.Close>
              <button type="submit" aria-disabled={!name.trim() || save.isPending} className={`${btn} bg-accent-grad hover-accent-grad text-white`}>
                {save.isPending && <Spinner />}
                {project ? "Сохранить" : "Создать проект"}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function DeleteProjectDialog({ project, onClose }: { project: Project | null; onClose: () => void }) {
  const del = useDeleteProject();
  const navigate = useNavigate();
  const confirm = () => {
    if (!project) return;
    del.mutate(project.id, {
      onSuccess: () => {
        onClose();
        toast("Проект удалён, его чаты остались в общем списке");
        void navigate({ to: "/" });
      },
      onError: () => toast("Не удалось удалить проект. Попробуйте ещё раз."),
    });
  };
  return (
    <AlertDialog.Root open={!!project} onOpenChange={(o) => !o && onClose()}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className={overlay} />
        <AlertDialog.Content className={panel}>
          <AlertDialog.Title className="text-lg font-bold">Удалить проект?</AlertDialog.Title>
          <AlertDialog.Description className="mt-2 text-[15px] leading-[22px] text-sub">
            Инструкции и файлы проекта «{project?.name}» будут удалены. Чаты останутся и вернутся в общий список.
          </AlertDialog.Description>
          <div className="mt-6 flex justify-end gap-2">
            <AlertDialog.Cancel className={`${btn} text-ink-2 hover:bg-bg`}>Отмена</AlertDialog.Cancel>
            <button type="button" onClick={confirm} aria-disabled={del.isPending} className={`${btn} bg-danger text-white hover:bg-danger-hover`}>
              {del.isPending && <Spinner />}
              Удалить
            </button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
